/**
 * RESPONSIBILITY: the page an address opens when it names no hash route.
 *
 * Three ways in to the Austin Desi Christmas page, and they must agree: `acfi.cc/#adc`
 * (a hash route like every other), `acfi.cc/adc` (the clean path people type), and
 * `adc.acfi.cc` or `austindesichristmas.org` (a name of its own). The last two carry no hash, so without this they
 * would open the church's home page.
 *
 * A hash that names a page still wins: on adc.acfi.cc a link to `#events` goes where it
 * says. Only the empty or unknown hash falls back.
 *
 * Another standalone site is a line here and a page beside Adc, not a new mechanism.
 */
import { Page, routeOf, type Route } from './pages';

/**
 * Which page an address opens, by host. A key is either a whole domain (`www.` is ignored)
 * or just the first label, so `adc` covers adc.acfi.cc and anything shaped like it.
 * One line per standalone site.
 */
const HOSTS: Record<string, Page> = {
  adc: Page.Adc,
  'austindesichristmas.org': Page.Adc,
};
const PATHS: Record<string, Page> = { adc: Page.Adc };

/**
 * The route for the hash `useHash()` reported.
 *
 * `useHash()` answers '#home' when the address has no hash at all, so by the time a hash
 * reaches here "nobody asked for a page" and "somebody asked for home" look the same.
 * The address bar is asked directly to tell them apart; only a hash that is really there
 * is allowed to override where the address itself points.
 */
export const routeFor = (reported: string): Route => {
  const entry = entryPage();
  // Signing in sends the browser to '#home'. On the church's own address that is right;
  // here it would land an editor on the church's home page, away from the page they were
  // editing. '#home' means "no page named" wherever the address names one of its own.
  const named = window.location.hash && !(entry !== Page.Home && reported === '#home');
  return routeOf(named ? reported : '', entry);
};

export function entryPage(loc: Pick<Location, 'hostname' | 'pathname'> = window.location): Page {
  const host = loc.hostname.toLowerCase().replace(/^www\./, '');
  const byHost = HOSTS[host] ?? HOSTS[host.split('.')[0]];
  if (byHost) return byHost;
  const first = loc.pathname.split('/').filter(Boolean)[0]?.toLowerCase() ?? '';
  return PATHS[first] ?? Page.Home;
}
